import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhoneInput } from "@/components/PhoneInput";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { usePageSEO, buildHreflang } from "@/hooks/usePageSEO";
import { useCmsPageSEO } from "@/hooks/useCmsPageSEO";

const FORM_SLUG = "start-a-project";

interface FormField {
  id: string;
  field_type: string;
  label: string;
  label_ar: string | null;
  placeholder: string | null;
  placeholder_ar: string | null;
  help_text: string | null;
  help_text_ar: string | null;
  required: boolean;
  options: string[];
  options_ar: string[];
  sort_order: number;
  step: number;
}

interface FormData {
  id: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  thank_you_title: string | null;
  thank_you_title_ar: string | null;
  thank_you_message: string | null;
  thank_you_message_ar: string | null;
}

function getLocalizedField<T>(isAr: boolean, arVal: T | null, enVal: T): T {
  return isAr && arVal ? arVal : enVal;
}

export default function StartProject() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const t = (key: string) => getTranslation(key, lang);

  const [form, setForm] = useState<FormData | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const { canonical, hreflang } = buildHreflang("/start-a-project");
  useCmsPageSEO({
    title: isAr ? "ابدأ مشروعك | وافاي" : "Start a Project | Wavai",
    description: isAr
      ? "أخبرنا عن مشروعك وسيتواصل فريقنا معك لمناقشة كيف يمكننا المساعدة."
      : "Tell us about your project and our team will get back to you to discuss how we can help.",
    canonical,
    hreflang,
  });

  useEffect(() => {
    const load = async () => {
      const { data: formData } = await supabase
        .from("forms")
        .select("id, title, title_ar, description, description_ar, thank_you_title, thank_you_title_ar, thank_you_message, thank_you_message_ar")
        .eq("slug", FORM_SLUG)
        .eq("status", "published")
        .single();

      if (!formData) {
        setLoading(false);
        return;
      }
      setForm(formData as any);

      const { data: fieldsData } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formData.id)
        .order("sort_order", { ascending: true });

      const parsed = (fieldsData || []).map((f: any) => ({
        ...f,
        options: Array.isArray(f.options) ? (f.options as string[]) : [],
        options_ar: Array.isArray(f.options_ar) ? (f.options_ar as string[]) : [],
      }));
      setFields(parsed);
      setLoading(false);
    };
    load();
  }, [isAr]);

  const totalSteps = fields.length > 0 ? Math.max(...fields.map((f) => f.step)) : 1;
  const currentFields = fields.filter((f) => f.step === currentStep);
  const progress = (currentStep / totalSteps) * 100;

  const validateStep = useCallback(() => {
    const newErrors: Record<string, string> = {};
    currentFields.forEach((field) => {
      const val = values[field.id] || "";
      if (field.required && !val.trim()) {
        newErrors[field.id] = t("This field is required");
      }
      if (field.field_type === "email" && val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        newErrors[field.id] = t("Please enter a valid email");
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [currentFields, values, t]);

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep < totalSteps) {
      setCurrentStep((s) => s + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!form) return;
    setSubmitting(true);
    // Always store with English labels for consistency in admin
    const data: Record<string, string> = {};
    fields.forEach((f) => {
      if (values[f.id]) {
        // If Arabic option was selected, map back to English for storage
        const opts = f.options;
        const optsAr = f.options_ar;
        let storedValue = values[f.id];
        if (isAr && optsAr.length > 0) {
          const arIdx = optsAr.indexOf(storedValue);
          if (arIdx !== -1 && opts[arIdx]) {
            storedValue = opts[arIdx];
          }
        }
        data[f.label] = storedValue;
      }
    });

    const { error } = await supabase.from("form_submissions").insert({
      form_id: form.id,
      data_json: data,
      source_url: window.location.href,
    });

    setSubmitting(false);
    if (error) {
      toast({ title: t("Submission failed"), description: error.message, variant: "destructive" });
    } else {
      setSubmitted(true);
      supabase.functions.invoke("notify-form-submission", {
        body: { submission_data: data, form_title: form.title },
      }).catch((err) => console.warn("Notification failed:", err));
    }
  };

  const setValue = (fieldId: string, value: string) => {
    setValues((v) => ({ ...v, [fieldId]: value }));
    setErrors((e) => { const n = { ...e }; delete n[fieldId]; return n; });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-12 lg:py-32 bg-surface">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4">
                {t("Start a Project")}
              </h1>
              <p className="text-lg text-muted-foreground">
                {form ? getLocalizedField(isAr, form.description_ar, form.description || "") : t("Tell us about your project and our team will get back to you to discuss how we can help.")}
              </p>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="py-12 lg:py-32">
          <div className="container px-6">
            <div className="max-w-2xl mx-auto">
              {loading && (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {!loading && !form && (
                <div className="text-center py-20">
                  <p className="text-muted-foreground">{t("Form not available at the moment. Please try again later.")}</p>
                </div>
              )}

              {!loading && form && submitted && (
                <div className="text-center space-y-4 animate-in fade-in-0 zoom-in-95 duration-500 py-12">
                  <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                    <Check className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">
                    {getLocalizedField(isAr, form.thank_you_title_ar, form.thank_you_title || "Thank you!")}
                  </h2>
                  <p className="text-muted-foreground text-lg max-w-md mx-auto">
                    {getLocalizedField(isAr, form.thank_you_message_ar, form.thank_you_message || "Your response has been recorded.")}
                  </p>
                </div>
              )}

              {!loading && form && !submitted && (
                <div className="space-y-8">
                  {/* Progress */}
                  {totalSteps > 1 && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground font-medium">
                        {t("Step")} {currentStep} / {totalSteps}
                      </span>
                      <Progress value={progress} className="flex-1 h-1.5" />
                    </div>
                  )}

                  <div className="space-y-8 animate-in fade-in-0 slide-in-from-right-4 duration-300" key={currentStep}>
                    {currentFields.map((field) => {
                      const label = getLocalizedField(isAr, field.label_ar, field.label);
                      const placeholder = getLocalizedField(isAr, field.placeholder_ar, field.placeholder || "");
                      const helpText = getLocalizedField(isAr, field.help_text_ar, field.help_text || "");
                      const options = isAr && field.options_ar.length > 0 ? field.options_ar : field.options;

                      return (
                        <div key={field.id} className="space-y-3">
                          <div>
                            <Label className="text-base font-medium">
                              {label}
                              {field.required && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {helpText && (
                              <p className="text-sm text-muted-foreground mt-1">{helpText}</p>
                            )}
                          </div>

                          {renderField(field, values[field.id] || "", (v) => setValue(field.id, v), placeholder, options, isAr, t)}

                          {errors[field.id] && (
                            <p className="text-sm text-destructive">{errors[field.id]}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-4">
                    <div>
                      {currentStep > 1 && (
                        <Button variant="ghost" onClick={handleBack} className="gap-2">
                          {isAr ? (
                            <>{t("Back")} <ArrowRight className="h-4 w-4" /></>
                          ) : (
                            <><ArrowLeft className="h-4 w-4" /> {t("Back")}</>
                          )}
                        </Button>
                      )}
                    </div>
                    <Button
                      onClick={handleNext}
                      disabled={submitting}
                      className="gap-2 min-w-[140px]"
                      size="lg"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : currentStep === totalSteps ? (
                        <>{t("Submit")} <Check className="h-4 w-4" /></>
                      ) : (
                        <>
                          {isAr ? (
                            <><ArrowLeft className="h-4 w-4" /> {t("Next")}</>
                          ) : (
                            <>{t("Next")} <ArrowRight className="h-4 w-4" /></>
                          )}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function renderField(
  field: FormField,
  value: string,
  onChange: (v: string) => void,
  placeholder: string,
  options: string[],
  isAr: boolean,
  t: (key: string) => string,
) {
  switch (field.field_type) {
    case "text":
    case "number":
    case "url":
      return (
        <Input
          type={field.field_type === "number" ? "number" : field.field_type === "url" ? "url" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-12 text-base"
        />
      );
    case "phone":
      return (
        <PhoneInput
          value={value}
          onChange={onChange}
          placeholder={placeholder || "5XX XXX XXXX"}
        />
      );
    case "email":
      return (
        <Input
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "you@example.com"}
          className="h-12 text-base"
        />
      );
    case "long_text":
      return (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="text-base"
        />
      );
    case "dropdown":
      return (
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className="h-12 text-base">
            <SelectValue placeholder={placeholder || (isAr ? "اختر..." : "Select an option...")} />
          </SelectTrigger>
          <SelectContent>
            {options.filter(Boolean).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case "multiple_choice":
      return (
        <RadioGroup value={value} onValueChange={onChange} className="space-y-2">
          {options.filter(Boolean).map((opt) => (
            <label
              key={opt}
              className={cn(
                "flex items-center gap-3 p-4 rounded-lg border border-border cursor-pointer transition-colors",
                value === opt ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              )}
            >
              <RadioGroupItem value={opt} />
              <span className="text-sm font-medium">{opt}</span>
            </label>
          ))}
        </RadioGroup>
      );
    case "yes_no":
      return (
        <div className="flex gap-3">
          {[isAr ? "نعم" : "Yes", isAr ? "لا" : "No"].map((opt) => (
            <Button
              key={opt}
              type="button"
              variant={value === opt ? "default" : "outline"}
              className="flex-1 h-12 text-base"
              onClick={() => onChange(opt)}
            >
              {opt}
            </Button>
          ))}
        </div>
      );
    default:
      return (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-12 text-base"
        />
      );
  }
}
